import ReactMarkdown from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import remarkGfm from 'remark-gfm'
import { classNames } from '@/ui/utils/main'
import { parseImageSizeTitle } from '@/ui/components/common/extensions/sized-image.extension'
import { ImageLightbox } from '@/ui/components/common/ImageLightbox'

interface MarkdownViewProps {
  /** Markdown source to render (may also be legacy plain text). */
  content: string
  /**
   * Compact mode is used in card/tile/summary contexts: images are
   * suppressed entirely so the surrounding layout stays predictable.
   */
  compact?: boolean
  className?: string
  /**
   * `'default'` (the implicit default) keeps the existing card/panel
   * styling untouched. `'article'` is used by read mode's blog-post layout:
   * a wider measure, serif body text, more generous spacing and justified
   * running text (paragraphs, list items, quotes — never headings or table
   * cells), hyphenated so justification doesn't open rivers of whitespace. Because a
   * page in article mode already has its own `<h1>` (the investigation/lead
   * title), markdown headings are downshifted one extra level in the DOM
   * (h1→h4, h2→h5, h3→h6) to keep a single, valid heading outline while
   * still looking like a heading visually.
   */
  variant?: 'default' | 'article'
}

/**
 * Read-only markdown renderer shared by every description/notes field.
 *
 * Security: intentionally does NOT use `rehype-raw` and does NOT override
 * react-markdown's default `urlTransform`. Those defaults keep raw HTML
 * inert and neutralise `javascript:` URLs in links/images — this is the
 * XSS boundary now that descriptions are user-authored markdown rendered
 * on public pages. Do not add either without a security review.
 *
 * `remark-breaks` makes a single `\n` render as a `<br>`, matching the
 * legacy `TextWithLinks` behaviour so old plain-text rows don't reflow.
 *
 * Embedded images (every mode but `compact`, which drops them) are wrapped in
 * `ImageLightbox`, so a click — or Enter/Space on the focused trigger — shows
 * the image much bigger in an overlay. The lightbox is handed the same
 * already-sanitised `src` react-markdown provides; no raw-HTML path is added.
 *
 * Heading margins are deliberately symmetric (`mt-N mb-N`, same N) at every
 * level and in both variants: a heading gets as much air below it, before its
 * own content, as it gets above it.
 */
export function MarkdownView({
  content,
  compact,
  className,
  variant = 'default',
}: MarkdownViewProps) {
  const isArticle = variant === 'article'

  return (
    <div className={classNames('text-wrap-anywhere', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{
          h1: ({ children }) =>
            isArticle ? (
              <h4 className="text-3xl font-display font-bold mt-8 mb-8 text-foreground">
                {children}
              </h4>
            ) : (
              <h1 className="text-xl font-bold font-serif mt-4 mb-4 text-foreground">{children}</h1>
            ),
          h2: ({ children }) =>
            isArticle ? (
              <h5 className="text-2xl font-display font-semibold mt-6 mb-6 text-foreground">
                {children}
              </h5>
            ) : (
              <h2 className="text-lg font-semibold font-serif mt-3 mb-3 text-foreground">
                {children}
              </h2>
            ),
          h3: ({ children }) =>
            isArticle ? (
              <h6 className="text-xl font-display font-bold mt-5 mb-5 text-foreground">
                {children}
              </h6>
            ) : (
              <h3 className="text-base font-semibold mt-2 mb-2 text-foreground">{children}</h3>
            ),
          p: ({ children }) => (
            <p
              className={
                isArticle
                  ? 'mb-6 text-xl leading-relaxed font-serif text-justify hyphens-auto text-foreground/90'
                  : 'mb-2 text-sm leading-relaxed text-foreground/80'
              }
            >
              {children}
            </p>
          ),
          strong: ({ children }) => (
            <strong className="font-semibold text-foreground">{children}</strong>
          ),
          em: ({ children }) => (
            <em className={isArticle ? 'italic text-foreground/90' : 'italic text-foreground/80'}>
              {children}
            </em>
          ),
          del: ({ children }) => (
            <del className="line-through text-muted-foreground">{children}</del>
          ),
          u: ({ children }) => (
            <u
              className={
                isArticle ? 'underline text-foreground/90' : 'underline text-foreground/80'
              }
            >
              {children}
            </u>
          ),
          ul: ({ children }) => (
            <ul
              className={
                isArticle
                  ? 'list-disc pl-6 mb-6 space-y-1.5 font-serif text-xl text-foreground/90'
                  : 'list-disc pl-5 mb-2 space-y-0.5 text-sm text-foreground/80'
              }
            >
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol
              className={
                isArticle
                  ? 'list-decimal pl-6 mb-6 space-y-1.5 font-serif text-xl text-foreground/90'
                  : 'list-decimal pl-5 mb-2 space-y-0.5 text-sm text-foreground/80'
              }
            >
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li
              className={
                isArticle
                  ? 'text-xl leading-relaxed text-justify hyphens-auto'
                  : 'text-sm leading-relaxed'
              }
            >
              {children}
            </li>
          ),
          blockquote: ({ children }) => (
            <blockquote
              className={
                isArticle
                  ? 'border-l-[3px] border-primary pl-6 pr-2 my-8 font-display italic text-2xl leading-snug text-foreground/75 text-justify hyphens-auto'
                  : 'border-l-2 border-primary/50 pl-3 italic text-muted-foreground my-2 text-sm'
              }
            >
              {children}
            </blockquote>
          ),
          pre: ({ children }) => (
            <pre
              className={classNames(
                'bg-muted rounded p-3 overflow-x-auto',
                isArticle ? 'my-6' : 'my-2',
              )}
            >
              {children}
            </pre>
          ),
          code: ({ children, className: codeClassName }) => (
            <code
              className={classNames(
                'font-mono text-foreground/80',
                // Relative, not absolute: monospace at a fixed 12px would be
                // half the height of the article's 25.4px body copy.
                isArticle ? 'text-[0.85em]' : 'text-xs',
                codeClassName ? '' : 'bg-muted px-1.5 py-0.5 rounded',
              )}
            >
              {children}
            </code>
          ),
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={classNames(
                'text-primary text-wrap-anywhere',
                isArticle
                  ? 'underline decoration-1 decoration-primary/40 underline-offset-4 hover:decoration-primary'
                  : 'hover:underline',
              )}
            >
              {children}
            </a>
          ),
          hr: () => <hr className={classNames('border-border', isArticle ? 'my-10' : 'my-3')} />,
          // GFM tables can be wider than their container (many/long
          // columns) — scroll horizontally inside their own box rather than
          // forcing the whole page to scroll.
          table: ({ children }) => (
            <div className="overflow-x-auto my-4">
              <table
                className={classNames(
                  'w-full border-collapse',
                  isArticle ? 'text-xl font-serif' : 'text-sm',
                )}
              >
                {children}
              </table>
            </div>
          ),
          th: ({ children }) => (
            <th className="text-left font-semibold px-3 py-2 border-b border-border text-foreground">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-3 py-2 border-b border-border/50 text-foreground/80">{children}</td>
          ),
          // Compact contexts (tiles/cards) never render embedded images.
          img: compact
            ? () => null
            : ({ src, alt, title }) => {
                if (typeof src !== 'string') return null
                if (isArticle) {
                  // Read mode renders every embedded image at the same
                  // homogeneous size, deliberately ignoring any per-image
                  // `=WxH` marker set by the editor's resize handles —
                  // otherwise the article mixes thumbnail-sized and
                  // full-bleed images depending on how each was authored.
                  //
                  // That homogeneous size is responsive: 70% of the reading
                  // measure is right on a wide screen and far too small on a
                  // phone, where the column is already only ~335px. Below
                  // `sm:` the image therefore takes the full measure *plus*
                  // 12px on each side (`-mx-3`), bleeding into part of the
                  // shell's 20px gutter so only ~8px of side padding is left.
                  // The bleed is smaller than that gutter, so it can never
                  // push the page into horizontal scrolling.
                  return (
                    <ImageLightbox
                      src={src}
                      alt={alt ?? ''}
                      className="markdown-image-trigger my-8 focus-visible:ring-0 focus-visible:ring-offset-0"
                    >
                      {/* `aspect-video` reserves the image's box before it
                          loads (it's lazy-loaded), so a TOC anchor landing
                          further down the article isn't thrown off by layout
                          shift once the image arrives. */}
                      <img
                        src={src}
                        alt={alt ?? ''}
                        loading="lazy"
                        className="markdown-image block -mx-3 w-[calc(100%+1.5rem)] max-w-none aspect-video object-cover sm:mx-auto sm:w-[70%] sm:max-w-full rounded-md border border-border transition-opacity group-hover:opacity-90 group-focus-visible:ring-2 group-focus-visible:ring-ring group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-background"
                      />
                    </ImageLightbox>
                  )
                }
                // The `=WxH` size marker lives in `title` (set by the rich
                // text editor's resize handles) — apply it as a width and
                // never forward the raw marker to the DOM as a tooltip.
                const { width } = parseImageSizeTitle(title ?? null)
                return (
                  <ImageLightbox
                    src={src}
                    alt={alt ?? ''}
                    className="markdown-image-trigger my-2 focus-visible:ring-0 focus-visible:ring-offset-0"
                  >
                    <img
                      src={src}
                      alt={alt ?? ''}
                      loading="lazy"
                      style={width ? { width: `${width}px`, height: 'auto' } : undefined}
                      className="markdown-image max-w-full h-auto rounded-md border border-border transition-opacity group-hover:opacity-90 group-focus-visible:ring-2 group-focus-visible:ring-ring group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-background"
                    />
                  </ImageLightbox>
                )
              },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}
